import CacheBase2 from '../../services/engines/CacheBase2';
import WindowGeoObj from './Types/WindowGeoObj';

const _name = 'JxWindowCache';

/**
 * Caches window position/state per JxForm window, keyed on windowId (this.getId()).
 * Items are WindowGeoObj instances — CacheBase2.get()/addOrUpdate() key on rk,
 * windowId IS rk here, no override needed.
 */
class JxWindowCache extends CacheBase2 {
    initialize() {
        this._name = _name;
    }

    /**
     * @param {string} windowId aka panel id
     * @returns {WindowGeoObj|null}
     */
    getByWindowId = (windowId) => {
        try {
            return this.get(windowId);
        } catch (error) {
            this.logError('getByWindowId', error.message);
            return null;
        }
    };

    /**
     * Merges patch onto the existing WindowGeoObj (or a fresh one) and re-wraps
     * the result in a new WindowGeoObj — spreading a class instance loses its
     * prototype methods, so the merge always happens on plain fields, then
     * gets re-instantiated. Never spread-assign a WindowGeoObj directly.
     * @param {string} windowId
     * @param {object} patch - partial, e.g. { posX, posY }
     * @returns {WindowGeoObj|null}
     */
    setByWindowId = (windowId, patch) => {
        try {
            const existing = this.get(windowId);
            const merged = { ...(existing ?? { rk: windowId }), ...patch };

            if (!merged.tabId) {
                merged.tabId = this.getTabId();
            }
            merged.ems = Date.now();

            const geo = new WindowGeoObj(merged);
            this.addOrUpdate([geo]);

            return geo;
        } catch (error) {
            this.logError('setByWindowId', error.message);
            return null;
        }
    };

    /**
     * Replaces the full cache from a raw BE payload — wraps every item in
     * a WindowGeoObj so cache contents are never plain objects, even
     * straight after a fresh fetch. Use instead of the inherited setCache()
     * for BE-sourced data.
     * @param {object[]} items - raw items from windowManagerService.getWindowPositions
     */
    hydrate = (items) => {
        try {
            const geos = (items ?? []).map((item) => new WindowGeoObj(item));
            this.setCache(geos);
        } catch (error) {
            this.logError('hydrate', error.message);
        }
    };
}

const jxWindowCache = new JxWindowCache();
export default jxWindowCache;
